/**
 * Image asset cache — keyed per HUB so images from different hubs never
 * collide, and persisted to IndexedDB so the cache survives app restarts.
 *
 * Why per-hub keys:
 *   - Image ids / S3 keys can collide across hubs (a restored backup shares
 *     UUIDs with the source hub). A flat cache keyed by id alone served the
 *     previous hub's blob for the new hub's same id — room backgrounds showed
 *     wrong / device-status images, etc.
 *   - Namespacing by the active hub (getHubSessionKey: `hub:<cloudHubId>` for
 *     cloud/WebRTC, `local:<host>:<port>` for direct) means switching hubs
 *     needs NO cache invalidation: each hub keeps its own images, and
 *     returning to a hub reuses them.
 *
 * Why IndexedDB:
 *   - URL.createObjectURL blobs only live for the current renderer. Storing
 *     the raw blob lets us recreate the object URL after an app restart.
 *   - Entries expire after TTL_MS (image content is stable per id/key, but a
 *     TTL avoids permanently serving long-deleted images).
 */

import { defineStore } from 'pinia';
import { ref } from 'vue';
import api from '@/services/api';
import { getHubSessionKey } from '@/config/runtime';

type ImageVariant = 'original' | 'thumbnail';

interface CachedImageAsset {
  url: string;
  fetchedAt: number;
}

interface StoredImageBlob {
  blob: Blob;
  fetchedAt: number;
}

/** How long persisted blobs stay valid before a re-fetch. */
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
/** Soft cap for the in-memory cache (oldest evicted past this). */
const MAX_MEMORY_ENTRIES = 800;

const IDB_NAME = 'otto-image-assets-v2';
const IDB_STORE = 'blobs';

function normalizeImageId(imageId: unknown): string {
  return String(imageId ?? '').trim();
}

function normalizeS3Key(s3Key: unknown): string {
  return String(s3Key ?? '').trim();
}

/** Stable per-hub namespace (see getHubSessionKey in config/runtime). */
function hubNamespace(): string {
  try {
    return getHubSessionKey() || 'local';
  } catch {
    return 'local';
  }
}

function cacheKey(ns: string, imageId: string, variant: ImageVariant): string {
  return `${ns}:${variant}:${imageId}`;
}

function s3CacheKey(ns: string, s3Key: string): string {
  return `${ns}:key:${s3Key}`;
}

// ── IndexedDB helpers (best-effort; failures degrade to memory-only) ────────

let idbPromise: Promise<IDBDatabase> | null = null;

function openIdb(): Promise<IDBDatabase> {
  if (idbPromise) return idbPromise;
  idbPromise = new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(IDB_STORE)) {
          db.createObjectStore(IDB_STORE);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } catch (error) {
      reject(error);
    }
  });
  // If opening fails, reset so a later call can retry.
  idbPromise.catch(() => {
    idbPromise = null;
  });
  return idbPromise;
}

async function idbGet(key: string): Promise<StoredImageBlob | null> {
  try {
    const db = await openIdb();
    return await new Promise((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).get(key);
      req.onsuccess = () => resolve((req.result as StoredImageBlob) ?? null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function idbPut(key: string, value: StoredImageBlob): Promise<void> {
  try {
    const db = await openIdb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    // persistence is best-effort
  }
}

/** Delete persisted blobs whose key starts with `prefix`. */
async function idbDeleteByPrefix(prefix: string): Promise<void> {
  try {
    const db = await openIdb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      const store = tx.objectStore(IDB_STORE);
      const req = store.openCursor();
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          if (String(cursor.key).startsWith(prefix)) cursor.delete();
          cursor.continue();
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    // ignore
  }
}

async function idbClearAll(): Promise<void> {
  try {
    const db = await openIdb();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    // ignore
  }
}

export const useImageAssetsStore = defineStore('imageAssets', () => {
  const assets = ref<Record<string, CachedImageAsset>>({});
  const inFlight = new Map<string, Promise<string | null>>();

  /** Evict oldest in-memory entries when over the soft cap. */
  function enforceMemoryCap(): void {
    const keys = Object.keys(assets.value);
    if (keys.length <= MAX_MEMORY_ENTRIES) return;
    const sorted = keys.map((k) => ({ k, t: assets.value[k].fetchedAt })).sort((a, b) => a.t - b.t);
    for (const { k } of sorted.slice(0, keys.length - MAX_MEMORY_ENTRIES)) {
      const asset = assets.value[k];
      if (asset?.url) URL.revokeObjectURL(asset.url);
      delete assets.value[k];
    }
  }

  function getImageUrl(imageId: unknown, variant: ImageVariant = 'original'): string | null {
    const id = normalizeImageId(imageId);
    if (!id) return null;
    const ns = hubNamespace();
    return assets.value[cacheKey(ns, id, variant)]?.url ?? null;
  }

  function getImageUrlByS3Key(s3Key: unknown): string | null {
    const key = normalizeS3Key(s3Key);
    if (!key) return null;
    const ns = hubNamespace();
    return assets.value[s3CacheKey(ns, key)]?.url ?? null;
  }

  async function fetchImageUrlByS3Key(s3Key: unknown, force = false): Promise<string | null> {
    const key = normalizeS3Key(s3Key);
    if (!key) return null;
    const ns = hubNamespace();
    const memKey = s3CacheKey(ns, key);

    if (!force && assets.value[memKey]) return assets.value[memKey].url;
    if (!force && inFlight.has(memKey)) return inFlight.get(memKey)!;

    const request = (async () => {
      if (!force) {
        const stored = await idbGet(memKey);
        if (stored && Date.now() - stored.fetchedAt < TTL_MS) {
          const url = URL.createObjectURL(stored.blob);
          assets.value[memKey] = { url, fetchedAt: stored.fetchedAt };
          return url;
        }
      }
      try {
        const response = await api.get(`/images/by-key/content?key=${encodeURIComponent(key)}`, {
          responseType: 'blob',
        });
        const nextUrl = URL.createObjectURL(response.data);
        const previousUrl = assets.value[memKey]?.url;
        assets.value[memKey] = { url: nextUrl, fetchedAt: Date.now() };
        if (previousUrl && previousUrl !== nextUrl) URL.revokeObjectURL(previousUrl);
        enforceMemoryCap();
        void idbPut(memKey, { blob: response.data, fetchedAt: Date.now() });
        return nextUrl;
      } catch (error) {
        console.warn('[ImageAssets] fetchImageUrlByS3Key failed', { key, error });
        return assets.value[memKey]?.url ?? null;
      }
    })().finally(() => {
      inFlight.delete(memKey);
    });

    inFlight.set(memKey, request);
    return request;
  }

  async function fetchImageUrl(
    imageId: unknown,
    variant: ImageVariant = 'original',
    force = false,
  ): Promise<string | null> {
    const id = normalizeImageId(imageId);
    if (!id) return null;
    const ns = hubNamespace();
    const memKey = cacheKey(ns, id, variant);

    if (!force && assets.value[memKey]) return assets.value[memKey].url;
    if (!force && inFlight.has(memKey)) return inFlight.get(memKey)!;

    const request = (async () => {
      if (!force) {
        const stored = await idbGet(memKey);
        if (stored && Date.now() - stored.fetchedAt < TTL_MS) {
          const url = URL.createObjectURL(stored.blob);
          assets.value[memKey] = { url, fetchedAt: stored.fetchedAt };
          return url;
        }
      }
      const query = variant === 'thumbnail' ? '?variant=thumbnail' : '';
      try {
        const response = await api.get(`/images/${encodeURIComponent(id)}/content${query}`, { responseType: 'blob' });
        const nextUrl = URL.createObjectURL(response.data);
        const previousUrl = assets.value[memKey]?.url;
        assets.value[memKey] = { url: nextUrl, fetchedAt: Date.now() };
        if (previousUrl && previousUrl !== nextUrl) URL.revokeObjectURL(previousUrl);
        enforceMemoryCap();
        void idbPut(memKey, { blob: response.data, fetchedAt: Date.now() });
        return nextUrl;
      } catch {
        return assets.value[memKey]?.url ?? null;
      }
    })().finally(() => {
      inFlight.delete(memKey);
    });

    inFlight.set(memKey, request);
    return request;
  }

  /**
   * Raw bytes for one image — for callers that need the file itself (e.g.
   * copying an existing gallery image into an entity upload). Reuses the
   * persisted blob when still fresh; image content is immutable per id.
   */
  async function fetchImageBlob(
    imageId: unknown,
    variant: ImageVariant = 'original',
  ): Promise<Blob | null> {
    const id = normalizeImageId(imageId);
    if (!id) return null;
    const memKey = cacheKey(hubNamespace(), id, variant);

    const stored = await idbGet(memKey);
    if (stored && Date.now() - stored.fetchedAt < TTL_MS) return stored.blob;

    const query = variant === 'thumbnail' ? '?variant=thumbnail' : '';
    try {
      const response = await api.get(`/images/${encodeURIComponent(id)}/content${query}`, {
        responseType: 'blob',
      });
      void idbPut(memKey, { blob: response.data, fetchedAt: Date.now() });
      return response.data;
    } catch (error) {
      console.warn('[ImageAssets] fetchImageBlob failed', { id, error });
      return null;
    }
  }

  function clear(imageId?: unknown, variant: ImageVariant = 'original') {
    if (imageId !== undefined) {
      const id = normalizeImageId(imageId);
      const key = cacheKey(hubNamespace(), id, variant);
      const asset = assets.value[key];
      if (asset?.url) URL.revokeObjectURL(asset.url);
      delete assets.value[key];
      inFlight.delete(key);
      void idbDeleteByPrefix(key);
      return;
    }

    for (const asset of Object.values(assets.value)) {
      URL.revokeObjectURL(asset.url);
    }
    assets.value = {};
    inFlight.clear();
    void idbClearAll();
  }

  /**
   * Drop only the CURRENT hub's cached images (memory + persisted). Intended
   * for manual/admin eviction, not for automatic hub switching — with per-hub
   * namespacing the cache is preserved across hub switches by design.
   */
  async function resetForHubSwitch(): Promise<void> {
    const ns = hubNamespace();
    const prefix = `${ns}:`;
    const toDrop = Object.keys(assets.value).filter((k) => k.startsWith(prefix));
    for (const k of toDrop) {
      const asset = assets.value[k];
      if (asset?.url) URL.revokeObjectURL(asset.url);
      delete assets.value[k];
    }
    await idbDeleteByPrefix(prefix);
  }

  return {
    assets,
    getImageUrl,
    fetchImageUrl,
    fetchImageBlob,
    getImageUrlByS3Key,
    fetchImageUrlByS3Key,
    clear,
    resetForHubSwitch,
  };
});
